/**
 * Lightweight AppSync real-time client using the graphql-ws sub-protocol.
 *
 * Implements the AWS AppSync WebSocket handshake (base64-encoded auth headers
 * in the connection URL) and message protocol (connection_init/ack, start/stop,
 * ka keep-alives). Uses API key authentication.
 *
 * Protocol reference:
 * https://docs.aws.amazon.com/appsync/latest/devguide/real-time-websocket-client.html
 *
 * This exists instead of @aws-amplify/api (~38KB gzipped + core dependency) to
 * keep the SDK dependency-free. Consider migrating to Amplify if you need
 * IAM/Cognito auth, multiple auth modes, or offline support.
 */

export type SubscriptionHandler = (data: unknown) => void;

export interface AppSyncClientOptions {
  /** AppSync HTTP endpoint URL (https://<id>.appsync-api.<region>.amazonaws.com/graphql) */
  appSyncUrl: string;
  /** API key for authentication */
  apiKey: string;
  /** Maximum number of reconnection attempts (default: 5) */
  maxRetries?: number;
  /** Called when the connection is established and acknowledged */
  onConnected?: () => void;
  /** Called when the connection is closed */
  onDisconnected?: () => void;
  /** Called when a connection error occurs */
  onError?: (error: Error) => void;
}

export interface AppSyncSubscription {
  /** Unsubscribe and stop receiving data for this subscription */
  unsubscribe: () => void;
}

export interface AppSyncClient {
  /** Establish the WebSocket connection to AppSync real-time endpoint */
  connect: () => void;
  /** Close the connection and clean up */
  disconnect: () => void;
  /** Subscribe to a GraphQL subscription */
  subscribe: (
    query: string,
    variables: Record<string, unknown>,
    handler: SubscriptionHandler,
  ) => AppSyncSubscription;
  /** Whether the connection is active and acknowledged */
  isConnected: () => boolean;
}

const BACKOFF_DELAYS = [1000, 2000, 4000, 8000, 16_000];

// Derives wss:// real-time URL from the https:// HTTP endpoint
export const deriveRealtimeUrl = (httpUrl: string): string => {
  const url = new URL(httpUrl);
  url.host = url.host.replace('appsync-api', 'appsync-realtime-api');
  url.pathname = '/graphql';
  return url
    .toString()
    .replace('https://', 'wss://')
    .replace('http://', 'ws://');
};

export const extractHost = (httpUrl: string): string => {
  const url = new URL(httpUrl);
  return url.host;
};

const encodeHeader = (host: string, apiKey: string): string => {
  const header = JSON.stringify({ host, 'x-api-key': apiKey });
  return btoa(header);
};

export const createAppSyncClient = (
  options: AppSyncClientOptions,
): AppSyncClient => {
  const {
    appSyncUrl,
    apiKey,
    maxRetries = 5,
    onConnected,
    onDisconnected,
    onError,
  } = options;

  const host = extractHost(appSyncUrl);
  const realtimeUrl = deriveRealtimeUrl(appSyncUrl);

  let ws: WebSocket | null = null;
  let connected = false;
  let intentionallyClosed = false;
  let retryCount = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let keepAliveTimer: ReturnType<typeof setTimeout> | null = null;

  const subscriptions = new Map<
    string,
    {
      query: string;
      variables: Record<string, unknown>;
      handler: SubscriptionHandler;
    }
  >();

  const clearTimers = () => {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (keepAliveTimer !== null) {
      clearTimeout(keepAliveTimer);
      keepAliveTimer = null;
    }
  };

  const resetKeepAlive = (timeoutMs: number) => {
    if (keepAliveTimer !== null) {
      clearTimeout(keepAliveTimer);
    }
    keepAliveTimer = setTimeout(() => {
      // Keep-alive timeout — connection is stale, reconnect
      if (ws) {
        ws.close();
      }
    }, timeoutMs);
  };

  const registerSubscription = (
    id: string,
    query: string,
    variables: Record<string, unknown>,
  ) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const payload = {
      id,
      type: 'start',
      payload: {
        data: JSON.stringify({ query, variables }),
        extensions: {
          authorization: {
            host,
            'x-api-key': apiKey,
          },
        },
      },
    };

    ws.send(JSON.stringify(payload));
  };

  const resubscribeAll = () => {
    for (const [id, sub] of subscriptions) {
      registerSubscription(id, sub.query, sub.variables);
    }
  };

  const createConnection = () => {
    const headerEncoded = encodeHeader(host, apiKey);
    const connectionUrl = `${realtimeUrl}?header=${headerEncoded}&payload=e30=`;

    ws = new WebSocket(connectionUrl, ['graphql-ws']);

    ws.addEventListener('open', () => {
      ws?.send(JSON.stringify({ type: 'connection_init' }));
    });

    ws.addEventListener('message', (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data as string) as {
          type: string;
          id?: string;
          payload?: { connectionTimeoutMs?: number; data?: unknown };
        };

        switch (message.type) {
          case 'connection_ack': {
            connected = true;
            retryCount = 0;
            const timeoutMs =
              (message.payload?.connectionTimeoutMs as number | undefined) ??
              300_000;
            resetKeepAlive(timeoutMs);
            onConnected?.();
            resubscribeAll();
            break;
          }

          case 'ka': {
            // Keep-alive — reset the timeout
            const timeoutMs = 300_000;
            resetKeepAlive(timeoutMs);
            break;
          }

          case 'data': {
            const id = message.id;
            if (id && subscriptions.has(id)) {
              const sub = subscriptions.get(id)!;
              sub.handler(message.payload?.data);
            }
            break;
          }

          case 'start_ack': {
            break;
          }

          case 'error': {
            const id = message.id;
            if (id && subscriptions.has(id)) {
              onError?.(
                new Error(
                  `Subscription error: ${JSON.stringify(message.payload)}`,
                ),
              );
            } else {
              onError?.(new Error(JSON.stringify(message.payload)));
            }
            break;
          }

          default: {
            break;
          }
        }
      } catch {
        // Ignore unparseable messages
      }
    });

    ws.addEventListener('close', () => {
      connected = false;
      clearTimers();
      onDisconnected?.();

      if (!intentionallyClosed) {
        if (retryCount >= maxRetries) {
          onError?.(new Error('Max reconnection attempts reached'));
          return;
        }

        const delay = BACKOFF_DELAYS[retryCount] ?? BACKOFF_DELAYS.at(-1)!;
        retryCount++;
        reconnectTimer = setTimeout(() => createConnection(), delay);
      }
    });

    ws.addEventListener('error', () => {
      onError?.(new Error('AppSync WebSocket connection error'));
    });
  };

  return {
    connect: () => {
      if (
        ws?.readyState === WebSocket.OPEN ||
        ws?.readyState === WebSocket.CONNECTING
      ) {
        return;
      }
      intentionallyClosed = false;
      createConnection();
    },

    disconnect: () => {
      intentionallyClosed = true;
      clearTimers();
      retryCount = 0;
      connected = false;

      // Unsubscribe all active subscriptions
      if (ws && ws.readyState === WebSocket.OPEN) {
        for (const id of subscriptions.keys()) {
          ws.send(JSON.stringify({ id, type: 'stop' }));
        }
      }

      subscriptions.clear();

      if (ws) {
        ws.close();
        ws = null;
      }
    },

    subscribe: (
      query: string,
      variables: Record<string, unknown>,
      handler: SubscriptionHandler,
    ): AppSyncSubscription => {
      const id = crypto.randomUUID();

      subscriptions.set(id, { query, variables, handler });

      // If already connected, register immediately
      if (connected) {
        registerSubscription(id, query, variables);
      }

      return {
        unsubscribe: () => {
          subscriptions.delete(id);
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ id, type: 'stop' }));
          }
        },
      };
    },

    isConnected: () => connected,
  };
};

// --- HTTP Query utility (for one-shot GraphQL queries) ---

export interface AppSyncQueryResponse<T = unknown> {
  data?: T;
  errors?: { message: string }[];
}

export const appSyncQuery = async <T = unknown>(
  appSyncUrl: string,
  apiKey: string,
  query: string,
  variables: Record<string, unknown>,
): Promise<AppSyncQueryResponse<T>> => {
  const response = await fetch(appSyncUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`AppSync query failed (${response.status}): ${text}`);
  }

  return (await response.json()) as AppSyncQueryResponse<T>;
};
